import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { GitCompare, Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCompare } from "@/context/CompareContext";
import { NAV } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Header = () => {
  const { t } = useLang();
  const { slugs } = useCompare();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Auto-close the mobile drawer whenever the route changes (after a nav click).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open so the page doesn't scroll behind it.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-[#2563EB]" : "text-[#64748B] hover:text-[#2563EB]"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block px-4 py-3 text-base rounded-lg transition-colors ${
      isActive
        ? "bg-[#2563EB]/10 text-[#2563EB] font-semibold"
        : "text-[#0F172A] hover:bg-[#F1F5F9]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]">
      <div className="hac-container py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2 group shrink-0">
          <span className="font-heading text-xl md:text-2xl tracking-tight">
            <span className="text-[#0F172A]">Horse</span>
            <span className="text-[#2563EB]">App</span>
            <span className="text-[#0F172A]">Compare</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/apps" data-testid={NAV.directory} className={linkClass}>{t.nav.directory}</NavLink>
          <NavLink to="/rankings" data-testid={NAV.rankings} className={linkClass}>{t.nav.rankings}</NavLink>
          <NavLink to="/categories" data-testid={NAV.categories} className={linkClass}>{t.nav.categories}</NavLink>
          <NavLink to="/compare" data-testid={NAV.compare} className={linkClass}>
            {t.nav.compare}
            {slugs.length > 0 && (
              <span data-testid={NAV.compareBadge} className="ml-1 inline-flex items-center justify-center w-5 h-5 bg-[#2563EB] text-white text-[10px] font-semibold rounded-full">
                {slugs.length}
              </span>
            )}
          </NavLink>
          <NavLink to="/advisor" data-testid={NAV.advisor} className={linkClass}>{t.nav.advisor}</NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={linkClass}>Blog</NavLink>
          <NavLink
            to="/sponsor"
            data-testid="nav-sponsor"
            className={({ isActive }) =>
              `text-sm tracking-wide transition-colors px-3 py-1.5 rounded-full ${
                isActive ? "bg-[#2563EB] text-white" : "border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
              }`
            }
          >
            {t.sponsor.nav}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to="/compare"
            className="md:hidden inline-flex items-center gap-1 text-[#2563EB]"
            data-testid="nav-compare-mobile"
          >
            <GitCompare size={18} />
            {slugs.length > 0 && <span className="text-xs font-semibold">{slugs.length}</span>}
          </Link>
          <LanguageSwitcher />
          {/* Mobile menu trigger — only renders < md */}
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]"
            data-testid="nav-mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — slides in below the header bar */}
      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 top-[64px] bg-black/30 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="md:hidden absolute left-0 right-0 top-full bg-white border-b border-[#E2E8F0] shadow-lg z-50 max-h-[calc(100vh-64px)] overflow-y-auto"
            data-testid="nav-mobile-drawer"
          >
            <nav className="px-3 py-3 space-y-1">
              <NavLink to="/apps" className={mobileLinkClass} data-testid="nav-mobile-directory">{t.nav.directory}</NavLink>
              <NavLink to="/rankings" className={mobileLinkClass} data-testid="nav-mobile-rankings">{t.nav.rankings}</NavLink>
              <NavLink to="/categories" className={mobileLinkClass} data-testid="nav-mobile-categories">{t.nav.categories}</NavLink>
              <NavLink to="/compare" className={mobileLinkClass} data-testid="nav-mobile-compare">
                <span className="inline-flex items-center gap-2">
                  {t.nav.compare}
                  {slugs.length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-[#2563EB] text-white text-[10px] font-semibold rounded-full">{slugs.length}</span>
                  )}
                </span>
              </NavLink>
              <NavLink to="/advisor" className={mobileLinkClass} data-testid="nav-mobile-advisor">{t.nav.advisor}</NavLink>
              <NavLink to="/blog" className={mobileLinkClass} data-testid="nav-mobile-blog">Blog</NavLink>
              <div className="pt-2 mt-2 border-t border-[#E2E8F0]">
                <NavLink
                  to="/sponsor"
                  data-testid="nav-mobile-sponsor"
                  className={({ isActive }) =>
                    `block text-center py-3 rounded-full font-semibold text-sm ${
                      isActive ? "bg-[#2563EB] text-white" : "border border-[#2563EB] text-[#2563EB]"
                    }`
                  }
                >
                  {t.sponsor.nav}
                </NavLink>
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
